import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import {
  REPORT_DOCUMENT_WIDTH_DEFAULT,
  REPORT_SOURCES_WIDTH_DEFAULT,
  REPORT_TOOLS_MIN_WIDTH,
  clampReportToolWidth,
  displayedReportToolWidths,
  maxReportToolWidth,
  readReportToolOpen,
  readReportToolWidth,
  reportToolOccupied,
  writeReportToolOpen,
  writeReportToolWidth,
  type ReportToolColumn as ReportToolId,
} from '@/lib/patientReports/workspacePrefs';
import { cn } from '@/utils/cn';

function useReportToolsWide(): boolean {
  const query = `(min-width: ${REPORT_TOOLS_MIN_WIDTH}px)`;
  const [wide, setWide] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(query);
    const onChange = () => setWide(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return wide;
}

export function useReportWorkspace(userId: string | null | undefined) {
  const wide = useReportToolsWide();
  const ref = useRef<HTMLDivElement>(null);
  const [workspaceWidth, setWorkspaceWidth] = useState(() =>
    typeof window === 'undefined' ? 1440 : window.innerWidth,
  );
  const [sourcesOpen, setSourcesOpenState] = useState(() => readReportToolOpen(userId, 'sources'));
  const [documentOpen, setDocumentOpenState] = useState(() =>
    readReportToolOpen(userId, 'document'),
  );
  const [sourcesPref, setSourcesPref] = useState(() => readReportToolWidth(userId, 'sources'));
  const [documentPref, setDocumentPref] = useState(() => readReportToolWidth(userId, 'document'));

  useEffect(() => {
    setSourcesOpenState(readReportToolOpen(userId, 'sources'));
    setDocumentOpenState(readReportToolOpen(userId, 'document'));
    setSourcesPref(readReportToolWidth(userId, 'sources'));
    setDocumentPref(readReportToolWidth(userId, 'document'));
  }, [userId]);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      if (node.clientWidth > 0) setWorkspaceWidth(node.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [wide]);

  const sourcesPresence = wide ? (sourcesOpen ? 'open' : 'rail') : 'hidden';
  const documentPresence = wide ? (documentOpen ? 'open' : 'rail') : 'hidden';
  const widths = displayedReportToolWidths({
    sourcesPref,
    documentPref,
    workspaceWidth,
    sources: sourcesPresence,
    document: documentPresence,
  });
  const sourcesOccupied = reportToolOccupied(sourcesPresence, widths.sources);
  const documentOccupied = reportToolOccupied(documentPresence, widths.document);

  function remember(column: ReportToolId, open: boolean) {
    if (column === 'sources') setSourcesOpenState(open);
    else setDocumentOpenState(open);
    writeReportToolOpen(userId, column, open);
  }

  function rememberWidth(column: ReportToolId, next: number) {
    const width = clampReportToolWidth(
      next,
      workspaceWidth,
      column === 'sources' ? documentOccupied : sourcesOccupied,
      column === 'sources' ? REPORT_SOURCES_WIDTH_DEFAULT : REPORT_DOCUMENT_WIDTH_DEFAULT,
    );
    if (column === 'sources') setSourcesPref(width);
    else setDocumentPref(width);
    writeReportToolWidth(userId, column, width);
  }

  const style = (
    wide
      ? {
          ...(sourcesOpen ? { '--pr-L': `${widths.sources}px` } : null),
          ...(documentOpen ? { '--pr-R': `${widths.document}px` } : null),
        }
      : undefined
  ) as CSSProperties | undefined;

  return {
    ref,
    wide,
    className: cn('pr-tools', wide && 'is-wide'),
    style,
    sources: {
      open: sourcesOpen,
      setOpen: (open: boolean) => remember('sources', open),
      width: widths.sources,
      max: maxReportToolWidth(workspaceWidth, documentOccupied, REPORT_SOURCES_WIDTH_DEFAULT),
      setWidth: (next: number) => rememberWidth('sources', next),
      resetWidth: () => rememberWidth('sources', REPORT_SOURCES_WIDTH_DEFAULT),
    },
    document: {
      open: documentOpen,
      setOpen: (open: boolean) => remember('document', open),
      width: widths.document,
      max: maxReportToolWidth(workspaceWidth, sourcesOccupied, REPORT_DOCUMENT_WIDTH_DEFAULT),
      setWidth: (next: number) => rememberWidth('document', next),
      resetWidth: () => rememberWidth('document', REPORT_DOCUMENT_WIDTH_DEFAULT),
    },
  };
}
